// -----------------------------------------------------------------------------
// CompletionChecklist
//
// The "Wrap-up checklist" section of the COMPLETED-TREATMENT hub. Presentational
// only: it receives the resolved checklist state (groups / initialLoading /
// templatesEmpty) as props from CompletedTreatmentHub, which owns the
// useCompletionChecklist hook so it can refresh() after complete/undo. Renders
// the shared ChecklistMiniGrid.
//
// Graceful degradation:
//   - shows a slim skeleton while initialLoading
//   - renders nothing when templatesEmpty (no tasks-v2 / no templates) or empty
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { Box, Stack, Typography, Skeleton, Button } from '@mui/material';
import Iconify from 'src/components/Iconify';
import ChecklistMiniGrid from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/ChecklistMiniGrid';

export default function CompletionChecklist({ initialLoading, templatesEmpty, groups, onItemClick, onUndo, onSkip, onChangeDueDate, onPauseChecklist }) {
    // Loading state — slim skeleton so the hub doesn't jump.
    if (initialLoading) {
        return (
            <Box sx={{ mt: 2.5 }}>
                <Skeleton variant="rounded" height={56} sx={{ borderRadius: '14px' }} />
            </Box>
        );
    }

    // Nothing applicable — render nothing (no tasks-v2, no templates, or empty).
    if (templatesEmpty) return null;

    const { today = [], later = [], done = [] } = groups || {};
    const hasAnything = today.length + later.length + done.length > 0;
    if (!hasAnything) return null;

    // Pause is checklist-wide (the /pause/ endpoint pauses the whole task-group),
    // so it's a single header control — only when there are active items to pause.
    const hasActive = today.length + later.length > 0;

    return (
        <Box
            sx={{
                mt: 2.5,
                p: 3,
                bgcolor: '#ffffff',
                borderRadius: '14px',
                width: '100%',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}
        >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.018em', mb: 0.5 }}>
                        Wrap-up checklist
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.01em' }}>
                        Open items for this treatment — clear them before completing.
                    </Typography>
                </Box>
                {onPauseChecklist && hasActive && (
                    <Button
                        data-testid="pause-checklist-btn"
                        size="small"
                        onClick={onPauseChecklist}
                        startIcon={<Iconify icon="eva:pause-circle-outline" width={16} height={16} />}
                        sx={{
                            flexShrink: 0,
                            color: '#6e6e73',
                            textTransform: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            borderRadius: '8px',
                            px: 1.25,
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: '#1d1d1f' },
                        }}
                    >
                        Pause checklist
                    </Button>
                )}
            </Stack>
            <ChecklistMiniGrid
                groups={groups}
                onItemClick={onItemClick}
                onUndo={onUndo}
                onSkip={onSkip}
                onChangeDueDate={onChangeDueDate}
            />
        </Box>
    );
}

CompletionChecklist.propTypes = {
    initialLoading: PropTypes.bool,
    templatesEmpty: PropTypes.bool,
    groups: PropTypes.shape({
        today: PropTypes.array,
        later: PropTypes.array,
        done: PropTypes.array,
    }),
    onItemClick: PropTypes.func,
    onUndo: PropTypes.func,
    onSkip: PropTypes.func,
    onChangeDueDate: PropTypes.func,
    /** Checklist-wide pause (the /pause/ endpoint pauses the whole task-group). */
    onPauseChecklist: PropTypes.func,
};

CompletionChecklist.defaultProps = {
    initialLoading: false,
    templatesEmpty: false,
    groups: { today: [], later: [], done: [] },
    onItemClick: null,
    onUndo: null,
    onSkip: null,
    onChangeDueDate: null,
    onPauseChecklist: null,
};
